<template>
  <TWModal
    :title="expireNow ? $t('envelope.expireNow') : $t('envelope.expirySettings')"
    :show="show"
    :autoFocus="false"
    :loading="loading"
    @close="closeModal"
  >
    <template v-slot:content>
      <div v-if="!expireNow && envelope.next_expire">
        <div class="flex items-center">
          <InfoIndicator
            class="mr-sm"
            size="small"
          />
          <p>
            <span>
              {{ $t('envelope.expiryDue') }}
            </span>
            <span>
              {{ envelope.next_expire | formatDate('calendar') }}
            </span>
          </p>
        </div>
        <hr />
      </div>
      <p v-if="expireNow">{{ $t('envelope.expireNowWarning') }}</p>
      <div v-else>
        <p v-if="creating">{{ $t('envelope.expireWhenCreating') }}</p>
        <p v-else>{{ $t('envelope.expireWhen') }}</p>
        <ElDatePicker
          v-model="customExpiry"
          @change="expiryReset = true;"
          type="date"
          data-ec="envelope_expire_modal"
          data-et="date_picker"
          :format="dateFormat"
          :picker-options="pickerOptions"
          :placeholder="$t('functional.pickADay')">
        </ElDatePicker>
      </div>
    </template>

    <template v-slot:controls>
      <div class="w-full flex flex-col justify-between gap-lg sm:flex-row">
        <BaseButton
          v-if="!expireNow && !creating"
          variant="danger"
          @click.native="toggleExpireNow"
          data-ec="envelope_expire_modal"
          data-et="show_expire_now_button"
        >
          <span>{{ $t('envelope.expireNow') }}</span>
        </BaseButton>

        <div class="flex flex-col-reverse gap-base sm:block">
          <BaseButton
            v-if="expireNow"
            variant="invisible"
            @click.native="toggleExpireNow"
            data-ec="envelope_expire_modal"
            data-et="cancel_expire_now_button"
          >
            {{ $t('functional.actuallyNoThanks') }}
          </BaseButton>
          <BaseButton
            v-else
            variant="invisible"
            @click.native="closeModal"
            data-ec="envelope_expire_modal"
            data-et="cancel_button"
          >
            {{ $t('functional.cancel') }}
          </BaseButton>

          <BaseButton
            v-if="expireNow"
            @click.native="expireEnvelopeNow"
            variant="danger"
            data-ec="envelope_expire_modal"
            data-et="expire_now_button"
          >
            {{ $t('envelope.confirmExpireNow') }}
          </BaseButton>
          <BaseButton
            v-else
            @click.native="setExpiry"
            :disabled="!expiryReset"
            data-ec="envelope_expire_modal"
            data-et="save_expiry_button"
          >
            {{ $t('functional.update') }}
          </BaseButton>
        </div>
      </div>
    </template>
  </TWModal>
</template>

<script>
import Moment from 'moment';
import localeDateFormatter from '@/utils/locale-date-formatter';
import dateFormat from '@/mixins/americanDateSwitcherMixin';

import TWModal from '@/components/tailwind/Modal.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import InfoIndicator from '@/components/library/indicators/InfoIndicator.vue';

export default {
  name: 'EnvelopeExpireModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    envelope: {
      type: [Object],
      required: true,
    },
    // TODO: remove when envelope module is fixed
    // and comes from one data source
    creating: {
      type: [Boolean],
      default: false,
    },
  },
  components: {
    TWModal,
    BaseButton,
    InfoIndicator,
  },
  data() {
    return {
      loading: false,
      expireNow: false,
      customExpiry: null,
      expiryReset: false,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now();
        },
        shortcuts: [
          {
            text: this.$t('envelope.expiryIntervals.1day'),
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime() + (3600 * 1000 * 24));
              picker.$emit('pick', date);
            },
          },
          {
            text: this.$t('envelope.expiryIntervals.1week'),
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime() + (3600 * 1000 * 24 * 7));
              picker.$emit('pick', date);
            },
          },
          {
            text: this.$t('envelope.expiryIntervals.30days'),
            onClick(picker) {
              const date = new Date();
              date.setTime(date.getTime() + (3600 * 1000 * 24 * 30));
              picker.$emit('pick', date);
            },
          },
        ],
      },
    };
  },
  created() {
    if (this.envelope.next_expire) {
      this.customExpiry = localeDateFormatter('date-field', this.envelope.next_expire);
    }
  },
  mixins: [dateFormat],
  computed: {
    customUnix() {
      if (this.customExpiry) {
        return Moment(this.customExpiry, 'YYYY-MM-DD').unix();
      }
      return 0;
    },
  },
  methods: {
    closeModal() {
      this.expireNow = false;
      this.expiryReset = false;
      this.$emit('close');
      this.loading = false;
    },
    toggleExpireNow() {
      this.expireNow = !this.expireNow;
    },
    async expireEnvelopeNow() {
      this.loading = true;
      try {
        await this.$store.dispatch('envelopes/EXPIRE_ENVELOPE', this.envelope.fingerprint);
      } finally {
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.success.expired', { name: 'envelope' }),
          icon: 'exclamation-circle',
          type: 'success',
        }));
        this.closeModal();
      }
    },
    async setExpiry() {
      this.loading = true;
      const expiry = {
        fingerprint: this.envelope.fingerprint,
        timestamp: this.customUnix,
      };
      try {
        await this.$store.dispatch('envelopes/SET_ENVELOPE_EXPIRY', { expiry, isCreating: this.creating });
      } finally {
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.success.updated', { name: 'expiry date' }),
          icon: 'check',
          type: 'success',
        }));
        this.closeModal();
      }
    },
  },
};
</script>

